import { useContext } from "react";
import { VersionChangesContext } from "../context/VersionChangesContext";
import type { RosterChangeCell } from "@/services/roster/changeNotices";
import RosterGrid from "@/components/feature/roster_create/components/roster-grid";
import { RosterGridType } from "@/components/feature/roster_create/components/roster-grid-type";
import {
  buildRosterTableLayoutConfig,
  ROSTER_DAY_CELL_PX,
} from "@/components/nurse/rosterColumnHoverLayout";
import ShiftCode from "@/components/shift/ShiftCode";

export function VersionRemovedRows({
  visibleNurseIds,
  daysInMonth,
  previousDays,
  showPreceptor,
  showLevel,
}: {
  readonly visibleNurseIds: readonly string[];
  readonly daysInMonth: number;
  readonly previousDays: number;
  readonly showPreceptor: boolean;
  readonly showLevel: boolean;
}) {
  const cells = useContext(VersionChangesContext);
  const groups = new Map<string, RosterChangeCell[]>();
  for (const cell of cells.values()) {
    if (cell.kind !== "removed" || visibleNurseIds.includes(cell.nurse_id))
      continue;
    const group = groups.get(cell.nurse_id) ?? [];
    group.push(cell);
    groups.set(cell.nurse_id, group);
  }
  if (!groups.size) return null;
  const layoutConfig = buildRosterTableLayoutConfig({
    showPreceptorColumn: showPreceptor,
    showLevelColumn: showLevel,
    showRowReorder: true,
  });
  return (
    <section
      className="mt-3 overflow-hidden rounded-lg border border-border bg-zinc-100"
      aria-label="근무 전부 삭제된 근무자"
    >
      {[...groups].map(([id, row]) => {
        const byDay = new Map(
          row.map((cell) => [Number(cell.date.slice(-2)), cell])
        );
        return (
          <RosterGrid
            key={id}
            gridType={RosterGridType.BODY}
            layoutConfig={layoutConfig}
            className="items-center border-b border-border last:border-b-0"
          >
            <div className="flex min-h-10 flex-wrap items-center gap-2 px-4 text-sm text-muted-foreground">
              <span>{row[0]?.nurse_name || id}</span>
              <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-[10px]">
                근무 전부 삭제
              </span>
            </div>
            <div className="flex py-1" data-testid="roster-version-removed-row">
              {previousDays > 0 && (
                <div style={{ width: previousDays * ROSTER_DAY_CELL_PX }} />
              )}{" "}
              {Array.from({ length: daysInMonth }, (_, index) => index + 1).map(
                (day) => {
                  const cell = byDay.get(day);
                  return (
                    <div
                      key={day}
                      style={{ width: ROSTER_DAY_CELL_PX }}
                      className="flex shrink-0 justify-center line-through opacity-60"
                      title={
                        cell
                          ? `${cell.date} · ${cell.before?.shift_id || "빈 칸"} → 빈 칸`
                          : undefined
                      }
                    >
                      {cell?.before && (
                        <ShiftCode
                          code={cell.before.shift_id}
                          color={cell.before.color ?? undefined}
                        />
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </RosterGrid>
        );
      })}
    </section>
  );
}
